import { Link } from 'react-router-dom';
import { ResponsiveLine } from '@nivo/line';
import { useTranslation } from 'react-i18next';

import { useTutorialContext } from '../../context/Tutorial';
import { useStrategiesContext } from './StrategiesContext';

import { ColorButton, ToggleButton } from '../UI';
import { Checkbox } from '../form';
import { Options } from './Options';
import { TTickerCurrency } from '../../types/model';

import { CHART_COLORS_HEX, COLORS } from '../../constants/colors';

import {
  computePercentage,
  formatDate,
  getOptionsList,
  getSymbol,
  getTotalValues,
} from '../../helpers/functions';

import { IStrategy } from '../../types/model';
import { Mode } from './types';
import { CustomNumber } from '../CustomNumber/CustomNumber';
import { ProfitLoss } from '../ProfitLoss';
import { useNavigate } from 'react-router';
import { Card } from '../UI';
const styleById = {
  dot: {
    strokeWidth: 10,
    strokeLinecap: 'round',
  },
  default: {
    strokeWidth: 1,
  },
};

const DashedLine = ({ series, lineGenerator, xScale, yScale }) => {
  return series.map(({ id, data, color }) => (
    <path
      key={id}
      d={lineGenerator(
        data.map((d) => ({
          x: xScale(d.data.x),
          y: yScale(d.data.y),
        }))
      )}
      fill="none"
      stroke={color}
      style={styleById[id] || styleById.default}
    />
  ));
};

export const StrategyGridCard = ({
  isSelectable = false,
  mode,
  selectedStrategies,
  showTooltip = false,
  strategy,
  handleOnOptionSelect = () => {},
  handleSelectedStrategies = () => {},
}: {
  isSelectable?: boolean;
  mode?: Mode;
  showTooltip?: boolean;
  selectedStrategies?: string[];
  strategy: IStrategy;
  handleOnOptionSelect?(f: string, s: IStrategy): void;
  handleSelectedStrategies?(id: string): void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { StrategiesFirstStrategyTooltip } = useTutorialContext();
  const {
    loadingStrategyActivationId,
    mode: contextMode,
    handleStrategyActivation,
  } = useStrategiesContext();

  !mode && (mode = contextMode);

  const {
    active,
    archived,
    color,
    id,
    currency,
    name,
    updatedAt,
    tags,
    strategyAssets: { items },
  } = strategy;

  const symbol = getSymbol(currency)!;
  const optionsList = getOptionsList(archived);
  const {
    totalValue: lastTotalValue,
    variations: { VAR_ALL: startValue },
  } = getTotalValues(
    strategy.strategyAssets.items,
    currency.toLocaleLowerCase() as TTickerCurrency,
    strategy.createdAt
  );
  const totalProfit = lastTotalValue - startValue;
  const percentage = computePercentage(startValue, lastTotalValue, 100);

  const lineData = items
    .reduce(
      (a, { asset: { history } }) => {
        history
          .slice(0, 7) // last 7 days
          .forEach(
            (
              { [currency.toLocaleLowerCase() as TTickerCurrency]: price },
              dayNumber
            ) => (a[dayNumber] += +price)
          );

        return a;
      },
      [...Array(7).fill(0)] as number[]
    )
    .map((val, x) => ({ x, y: val / (items.length || 1) }));

  const commonProperties = {
    enablePoints: false,
    margin: {
      top: 5,
      right: 5,
      bottom: 5,
      left: 5,
    },
    enableGridX: false,
    enableGridY: false,
    data: [
      {
        id: 'Last performance',
        data: lineData,
      },
      {
        id: 'dot',
        data: [
          { x: 6, y: lineData[6].y },
          { x: 6, y: lineData[6].y },
        ],
      },
    ],
  };

  const isChecked = selectedStrategies?.includes(id);
  const isDisabled = selectedStrategies?.length === 1 && !isChecked;

  return (
    <Card
      padding={false}
      className={`
        flex
        flex-col
        justify-between
        dark:bg-grey-80
        dark:text-white

        h-${mode === 'viewGrid' ? '48' : '96'}
        ${(!isSelectable || !isChecked) && 'border-grey-30 dark:border-grey-70'}
      `}
      header={
        <>
          {/* CARD HEADER */}
          <div className={'flex w-full py-2 px-4'}>
            {/* strategy color & name */}
            <div
              className={
                'flex w-full items-center justify-start cursor-pointer flex-grow-0 overflow-hidden'
              }
              onClick={() => navigate('/strategies/' + strategy.id)}
            >
              {isSelectable && (
                <Checkbox
                  className={`mr-3`}
                  isCheck={isChecked}
                  isDisabled={isDisabled}
                  shape="square"
                  value={id}
                  onClick={handleSelectedStrategies}
                />
              )}
              <ColorButton radius={3} color={color} isReadOnly={true} />
              <h4 className={'pl-3 text-lg font-medium truncate'}>
                <Link to={'/strategies/' + id}>{name}</Link>
              </h4>
            </div>

            {/* ACTIVE & toggle & options*/}
            {!isSelectable && (
              <div className={'flex items-center w-auto justify-end ml-2'}>
                {showTooltip && (
                  <StrategiesFirstStrategyTooltip tooltipPopupClassname="w-52" />
                )}
                <div className="ml-1 mr-1 font-semibold text-xs">Active</div>

                <ToggleButton
                  onActivate={() => handleStrategyActivation(id, !active)}
                  onDeactivate={() => handleStrategyActivation(id, !active)}
                  isActivated={active}
                  isLoading={loadingStrategyActivationId === id}
                  isDisabled={!!loadingStrategyActivationId}
                />

                <Options
                  className="ml-2 cursor-pointer"
                  onSelect={(option) => handleOnOptionSelect(option, strategy)}
                  options={optionsList}
                />
              </div>
            )}
          </div>
        </>
      }
      footer={
        <>
          {/* FOOTER */}
          {mode === 'viewGraph' && (
            <div>
              {/* Date & Asset */}
              <div
                className={`
                  flex
                  w-full
                  px-4
                  py-2
                  items-center
                  justify-between
                  cursor-pointer
                `}
                onClick={() => navigate('/strategies/' + strategy.id)}
              >
                <div className="text-grey-50 text-xs">
                  {updatedAt && formatDate(updatedAt)}
                </div>
                <div className="flex text-xs">
                  {items.length}
                  <span className="pl-1.5 uppercase">
                    {t('strategies.asset')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      }
    >
      {/* CARD BODY */}
      <div
        className={
          'px-4 py-2 h-full flex flex-col justify-between cursor-pointer'
        }
        onClick={() => navigate('/strategies/' + strategy.id)}
      >
        {/* tags */}
        <div
          className={'flex text-xs font-medium items-center overflow-hidden '}
        >
          {tags.slice(0, 4).map((tag, i) => (
            <div
              key={tag + '_' + i}
              className={`
                rounded-xl
                text-xs
                bg-grey-5
                min-w-16
                px-3
                py-2
                mr-2
                uppercase
                truncate
                dark:bg-grey-70
              `}
            >
              {tag}
            </div>
          ))}
          {tags.length > 4 ? (
            <div className={'flex items-center font-semibold text-xs mr-2'}>
              {t('strategies.tags.more', {
                count: tags.length - 4,
              })}
            </div>
          ) : (
            <div></div>
          )}
        </div>

        {/* values */}
        <div className="flex items-center overflow-hidden mt-2">
          <CustomNumber
            className="text-2xl font-medium w-full"
            suffix={symbol}
            displayType={'text'}
            value={lastTotalValue}
          />
        </div>

        {/* Profit/Loss */}
        <div className="flex items-center text-sm mt-1">
          <div className="inline text-grey-50 mr-1">Total profit:</div>
          <ProfitLoss
            isRow
            valueClassName={'mr-2'}
            percentage={percentage}
            symbol={symbol}
            value={totalProfit}
            displayType={'text'}
          />
        </div>

        {mode === 'viewGraph' && (
          <>
            {/* CHART */}
            <div className="h-32 mt-2 flex items-center">
              <ResponsiveLine
                {...commonProperties}
                animate={false}
                curve="monotoneX"
                colors={() =>
                  CHART_COLORS_HEX[totalProfit < 0 ? 'NEGATIVE' : 'POSITIVE']
                }
                lineWidth={3}
                xScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                }}
                yScale={{ min: 'auto', type: 'linear', max: 'auto' }}
                axisLeft={{
                  tickSize: 0,
                  legendOffset: 12,
                }}
                axisBottom={{
                  tickSize: 0,
                  legendOffset: -12,
                }}
                layers={[
                  'grid',
                  'markers',
                  'areas',
                  // AreaLayer,
                  DashedLine,
                  'lines',
                  'slices',
                  'axes',
                  'points',
                  'legends',
                ]}
              />
            </div>

            {/* LEGEND */}
            <div className={'flex text-grey-50 text-xs items-center mt-1 '}>
              <div
                className={`
                h-1
                w-4
                bg-
                bg-${totalProfit < 0 ? 'negative' : 'positive'}
                rounded-full
                mr-2
                shadow-gray-50
                shadow-xl
              `}
              />
              {t('strategies.lastPerformance')}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};
